import { useCallback } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import DropdownMenuEllipsis, {
  DropdownMenuEllipsisProps,
} from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import useCanManageCloudIntegration from "shared/CloudIntegration/access/useCanManageCloudIntegration";

import { AwsIntegrationView } from "../../useSearchCloudIntegrations/gql";
import { showAwsIntegrationDeleteConfirmation } from "../DeleteConfirmation";
import { showAwsIntegrationDetailsDrawer } from "../DetailsDrawer";
import { showAwsIntegrationFormDrawer } from "../FormDrawer";

type CloudIntegrationsAwsActionsDropdownProps = {
  integration: AwsIntegrationView;
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
  hideSeeDetails?: boolean;
  onDeleteSuccess?: () => void;
  dotsSize?: DropdownMenuEllipsisProps["dotsSize"];
  refetchQueriesOnEdit: string[];
  refetchQueriesOnDelete?: string[];
};

// TODO: replace with AAC
const canDeleteCloudIntegration = (integration: AwsIntegrationView) => {
  return !integration.hasAttachedStacks;
};

const CloudIntegrationsAwsActionsDropdown = ({
  integration,
  trackAnalytics,
  hideSeeDetails,
  onDeleteSuccess,
  dotsSize = "small",
  refetchQueriesOnEdit,
  refetchQueriesOnDelete,
}: CloudIntegrationsAwsActionsDropdownProps) => {
  const canManageCloudIntegration = useCanManageCloudIntegration();

  const handleEditIntegration = useCallback(async () => {
    showAwsIntegrationFormDrawer({
      id: integration.id,
      integration,
      trackAnalytics,
      refetchQueriesOnEdit,
    });
  }, [integration, refetchQueriesOnEdit, trackAnalytics]);

  const handleDeleteIntegration = useCallback(() => {
    showAwsIntegrationDeleteConfirmation({
      integration,
      onDeleteSuccess,
      refetchQueriesOnDelete,
    });
  }, [integration, onDeleteSuccess, refetchQueriesOnDelete]);

  const handleSeeDetails = useCallback(() => {
    showAwsIntegrationDetailsDrawer({ id: integration.id, integration, trackAnalytics });
  }, [integration, trackAnalytics]);

  const hasAccessToManageIntegration = canManageCloudIntegration(integration);

  const canDeleteIntegration = canDeleteCloudIntegration(integration);

  return (
    <DropdownMenuEllipsis tooltip="Integration actions" dotsSize={dotsSize}>
      <CopyFieldDropdownMenuItem
        title="Copy ID"
        value={integration.id}
        onCopy={() => trackAnalytics("Copy ID clicked")}
      />
      {!hideSeeDetails && (
        <DropdownMenuItem onAction={handleSeeDetails}>See details</DropdownMenuItem>
      )}
      <DropdownMenuItem
        onAction={handleEditIntegration}
        tooltip={
          !hasAccessToManageIntegration
            ? "You don't have access to manage this integration"
            : undefined
        }
        isTooltipActive={!hasAccessToManageIntegration}
        isDisabled={!hasAccessToManageIntegration}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem
        onAction={handleDeleteIntegration}
        danger
        isDisabled={!hasAccessToManageIntegration || !canDeleteIntegration}
        tooltip={
          !hasAccessToManageIntegration
            ? "You don't have access to manage this integration"
            : "Cannot delete this integration because it is attached to a stack"
        }
        isTooltipActive={!hasAccessToManageIntegration || !canDeleteIntegration}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default CloudIntegrationsAwsActionsDropdown;
